import React, { useEffect, useState, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { canAccessBooklet } from './utils/bookletAccess';
import { SiteSettingsProvider } from './contexts/SiteSettingsContext';
import { Notifier, installAlertShim } from './lib/notify';
import { initNativePushListeners } from './utils/nativePushBridge';
import { BreadcrumbNamesProvider } from './components/layout/BreadcrumbContext';
// Kept eager: the login screen (most common first paint for a logged-out
// visitor) and the always-mounted agreement gate. Everything else is
// code-split with React.lazy so the initial JS bundle stays small and each
// route's chunk only downloads when it's first visited — a big first-load
// win on mobile connections.
import LoginPage from './pages/Login';
import AgreementGate from './components/AgreementGate';

// Persistent app shell: mounts the dashboard chrome (sidebar/header/bottom
// tabs) ONCE as a layout route, so navigating between pages swaps only the
// content area. Lazy like the pages — logged-out visitors never download it.
const PersistentShell = lazy(() => import('./components/layout/PersistentShell'));

const ForgotPasswordPage = lazy(() => import('./pages/ForgotPassword'));
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard'));
const StaffDashboard = lazy(() => import('./pages/staff/Dashboard'));
const TeacherDashboard = lazy(() => import('./pages/teacher/Dashboard'));
const StudentDashboard = lazy(() => import('./pages/student/Dashboard'));
const MarketingDashboard = lazy(() => import('./pages/marketing/Dashboard'));
const LeadsPage = lazy(() => import('./pages/admin/Leads'));
const ContactsPage = lazy(() => import('./pages/admin/Contacts'));
const RegistrationFormsPage = lazy(() => import('./pages/admin/RegistrationForms'));
const SurveysPage = lazy(() => import('./pages/admin/Surveys'));
const PublicSurveyPage = lazy(() => import('./pages/public/PublicSurvey'));
const PublicRegistration = lazy(() => import('./pages/public/PublicRegistration'));
const RegistrationCompletion = lazy(() => import('./pages/public/RegistrationCompletion'));
const Catalogue = lazy(() => import('./pages/public/Catalogue'));
const StudentsPage = lazy(() => import('./pages/admin/Students'));
const StudentFinance = lazy(() => import('./pages/admin/StudentFinance'));
const TeachersPage = lazy(() => import('./pages/admin/Teachers'));
const UsersPage = lazy(() => import('./pages/admin/Users'));
const PaymentsPage = lazy(() => import('./pages/admin/Payments'));
const PaymentPlansPage = lazy(() => import('./pages/admin/PaymentPlans'));
const MessagesPage = lazy(() => import('./pages/admin/Messages'));
const EmailDesignerPage = lazy(() => import('./pages/admin/EmailDesigner'));
const BookletEditorPage = lazy(() => import('./pages/admin/BookletEditor'));
const SettingsPage = lazy(() => import('./pages/admin/Settings'));
const CoursesPage = lazy(() => import('./pages/admin/Courses'));
const CourseDetail = lazy(() => import('./pages/admin/CourseDetail'));
const CohortsPage = lazy(() => import('./pages/admin/Cohorts'));
const CohortDetail = lazy(() => import('./pages/admin/CohortDetail'));
const NotificationsPage = lazy(() => import('./pages/admin/Notifications'));
const ZoomBackupPage = lazy(() => import('./pages/admin/ZoomBackup'));
const JobStatusPage = lazy(() => import('./pages/admin/JobStatus'));
const CourseCopyFileIncidentsPage = lazy(() => import('./pages/admin/CourseCopyFileIncidents'));
const BunnyMirrorPage = lazy(() => import('./pages/admin/BunnyMirror'));
const ZoomMeetingsPage = lazy(() => import('./pages/admin/ZoomMeetings'));
const AuditLogsPage = lazy(() => import('./pages/admin/AuditLogs'));
const AccessControlPage = lazy(() => import('./pages/admin/AccessControl'));
const LogsPage = lazy(() => import('./pages/admin/Logs'));
const RecycleBinPage = lazy(() => import('./pages/admin/RecycleBin'));
const ProgramsPage = lazy(() => import('./pages/admin/Programs'));
const TeacherStudentsPage = lazy(() => import('./pages/teacher/Students'));
const TeacherCoursesPage = lazy(() => import('./pages/teacher/Courses'));
const TeacherNotificationsPage = lazy(() => import('./pages/teacher/Notifications'));
const StudentCoursesPage = lazy(() => import('./pages/student/Courses'));
const SchedulePage = lazy(() => import('./pages/shared/Schedule'));
const GlobalSchedulePage = lazy(() => import('./pages/admin/GlobalSchedule'));
const CalendarEventsAdminPage = lazy(() => import('./pages/admin/CalendarEvents'));
const TeamMeetingsPage = lazy(() => import('./pages/admin/Meetings'));
const PrivateCalendarPage = lazy(() => import('./pages/admin/PrivateCalendar'));
const StaffAttendancePage = lazy(() => import('./pages/admin/StaffAttendance'));
const WorkReportsPage = lazy(() => import('./pages/shared/WorkReports'));
const WhatsappInboxPage = lazy(() => import('./pages/shared/WhatsappInboxPage'));
const CourseRecordings = lazy(() => import('./pages/shared/CourseRecordings'));
const CourseContent = lazy(() => import('./pages/shared/CourseContent'));
const CourseShell = lazy(() => import('./pages/shared/CourseShell'));
const CourseTabRedirect = lazy(() =>
  import('./pages/shared/CourseShell').then((m) => ({ default: m.CourseTabRedirect }))
);
const CourseOverview = lazy(() => import('./pages/shared/CourseOverview'));
const WatchRecording = lazy(() => import('./pages/shared/WatchRecording'));
const StudentPaymentsPage = lazy(() => import('./pages/student/Payments'));
const StudentGradesPage = lazy(() => import('./pages/student/Grades'));
const StudentCertificatesPage = lazy(() => import('./pages/student/Certificates'));
const StudentCertificateView = lazy(() => import('./pages/student/CertificateView'));
const CertificateManager = lazy(() => import('./pages/shared/CertificateManager'));
const CertificateEditor = lazy(() => import('./pages/shared/CertificateEditor'));
const StudentNotificationsPage = lazy(() => import('./pages/student/Notifications'));
const StudentProfilePage = lazy(() => import('./pages/student/Profile'));
const TeacherProfilePage = lazy(() => import('./pages/teacher/Profile'));
const AdminProfilePage = lazy(() => import('./pages/admin/Profile'));
const EnrollmentsPage = lazy(() => import('./pages/admin/Enrollments'));
const AssignmentsPage = lazy(() => import('./pages/shared/Assignments'));
const GradebookPage = lazy(() => import('./pages/shared/Gradebook'));
const CourseGradingPage = lazy(() => import('./pages/teacher/CourseGrading'));
const TakeExamPage = lazy(() => import('./pages/student/TakeExam'));
const TaskBoardPage = lazy(() => import('./pages/admin/TaskBoard'));
const DeletionRequestsPage = lazy(() => import('./pages/admin/DeletionRequests'));
const ContactPage = lazy(() => import('./pages/shared/Contact'));
const WebsiteDepartments = lazy(() => import('./pages/admin/website/Departments'));
const WebsiteNews = lazy(() => import('./pages/admin/website/News'));
const WebsiteBanners = lazy(() => import('./pages/admin/website/Banners'));
const WebsitePages = lazy(() => import('./pages/admin/website/Pages'));
const WebsiteLibrary = lazy(() => import('./pages/admin/website/Library'));
const WebsiteTestimonials = lazy(() => import('./pages/admin/website/Testimonials'));
const WebsiteJobs = lazy(() => import('./pages/admin/website/Jobs'));
const WebsiteSubmissions = lazy(() => import('./pages/admin/website/SubmissionsInbox'));
const WebsiteDisplayPlaylists = lazy(() => import('./pages/admin/website/DisplayPlaylists'));

// A retired page URL whose content now lives as a tab on the merged Schedule
// page. Preserves query params (e.g. ?new=meeting) while pinning the tab; the
// target route enforces roles.
const WebsiteSiteSettings = lazy(() => import('./pages/admin/website/SiteSettings'));
const NewsEventsPage = lazy(() => import('./pages/shared/NewsEvents'));
const LibraryPage = lazy(() => import('./pages/shared/Library'));
const AgreementsPage = lazy(() => import('./pages/admin/Agreements'));
const PricingProtocolPage = lazy(() => import('./pages/admin/PricingProtocol'));
const GraduationSlideshow = lazy(() => import('./pages/public/GraduationSlideshow'));
const OpenDay = lazy(() => import('./pages/public/OpenDay'));
const Tamkeen = lazy(() => import('./pages/public/Tamkeen'));

// ---- Cold-start chunk warmup -------------------------------------------
// For a logged-in user the old boot was a strict waterfall: bundle → auth
// check → download PersistentShell chunk → download dashboard chunk → fetch
// page data, each step showing its own spinner. Kick off the chunk downloads
// NOW (module parse time), in parallel with everything else — webpack shares
// the promise with the React.lazy() calls above, so by the time routes
// render the chunks are usually already in memory.
try {
  if (localStorage.getItem('token')) {
    import('./components/layout/PersistentShell').catch(() => {});
    const cached = JSON.parse(localStorage.getItem('user') || 'null');
    const rawRole = cached?.role?.name ?? cached?.role ?? cached?.roleName;
    const role = typeof rawRole === 'string' ? rawRole.trim().toLowerCase() : '';
    if (role === 'student') import('./pages/student/Dashboard').catch(() => {});
    else if (role === 'teacher') import('./pages/teacher/Dashboard').catch(() => {});
    else if (role === 'staff' || role === 'manager') import('./pages/staff/Dashboard').catch(() => {});
    else if (role === 'admin' || role === 'superadmin') import('./pages/admin/Dashboard').catch(() => {});
    else if (role === 'marketing') import('./pages/marketing/Dashboard').catch(() => {});
  }
} catch (_) {
  // localStorage unavailable (private mode) — skip warmup, normal lazy flow.
}

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    });
  }, [pathname]);
  return null;
};

const ProtectedRoute = ({ children, allowedRoles, capability }) => {
  const { user, isAuthenticated, loading } = useAuth();
  const location = useLocation();
  // Bounded grace period for the "authenticated but role not yet
  // resolved" window right after login. If the role still hasn't
  // appeared after this many milliseconds we fail closed (redirect to
  // /unauthorized) rather than spin forever.
  const [roleGraceExpired, setRoleGraceExpired] = useState(false);
  useEffect(() => {
    setRoleGraceExpired(false);
    const t = setTimeout(() => setRoleGraceExpired(true), 2000);
    return () => clearTimeout(t);
  }, [user, location.pathname]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="h-12 w-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
          <p className="text-muted-foreground">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Preserve the URL the user was trying to reach (path + query +
    // hash) so Login.jsx can send them back there after sign-in. This
    // is what makes /r/<token> share links land on the right recording
    // even when the recipient wasn't logged in yet.
    const next = `${location.pathname}${location.search}${location.hash}`;
    const qs = next && next !== '/' ? `?next=${encodeURIComponent(next)}` : '';
    return <Navigate to={`/login${qs}`} replace />;
  }

  if (capability && (capability !== 'bookletEditorAccess' || !canAccessBooklet(user))) {
    return <Navigate to="/unauthorized" replace />;
  }
  if (allowedRoles && allowedRoles.length > 0) {
    // Role can arrive as a plain string ("teacher") on fresh login, or
    // as a populated object ({ name: "teacher" }) after /auth/me
    // reshapes it. Accept both so a teacher doesn't bounce to 403 on
    // their very first navigation after sign-in.
    const rawRole = user?.role?.name ?? user?.role ?? user?.roleName;
    const userRole = typeof rawRole === 'string' ? rawRole.trim().toLowerCase() : '';
    const allowed = allowedRoles.map((r) => String(r).toLowerCase());
    // "Unknown role" is NOT the same as "forbidden" — for a short
    // window. Right after login the user object is briefly in a
    // transitional state (login response arrives before /auth/me
    // reshapes the role), and treating that gap as a 403 caused every
    // student to see "غير مصرح" flash for ~200ms before the dashboard
    // rendered. We hold on a spinner during the grace period; if the
    // role still hasn't resolved after 2s we fail closed and send the
    // user to /unauthorized so they're never stuck on an infinite
    // spinner.
    if (!userRole) {
      if (roleGraceExpired) {
        return <Navigate to="/unauthorized" replace />;
      }
      return (
        <div className="min-h-screen flex items-center justify-center bg-background">
          <div className="h-12 w-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
        </div>
      );
    }
    if (!allowed.includes(userRole)) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  return children;
};

const RoleBasedRedirect = () => {
  const { user, isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="h-12 w-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const rawRole = user?.role?.name ?? user?.role ?? user?.roleName;
  const role = typeof rawRole === 'string' ? rawRole.trim().toLowerCase() : '';
  switch (role) {
    case 'admin':
    case 'superadmin':
      return <Navigate to="/admin" replace />;
    case 'staff':
    case 'manager':
      return <Navigate to="/staff" replace />;
    case 'teacher':
      return <Navigate to="/teacher" replace />;
    case 'marketing':
      return <Navigate to="/marketing" replace />;
    case 'student':
      return <Navigate to="/student" replace />;
    default:
      return <Navigate to="/login" replace />;
  }
};

const CoursesRedirect = () => {
  const { user, isAuthenticated, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="h-12 w-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  const rawRole = user?.role?.name ?? user?.role ?? user?.roleName;
  const role = typeof rawRole === 'string' ? rawRole.trim().toLowerCase() : '';
  switch (role) {
    case 'admin':
    case 'superadmin':
      return <Navigate to="/admin/courses" replace />;
    case 'staff':
    case 'manager':
      return <Navigate to="/admin/courses" replace />;
    case 'teacher':
      return <Navigate to="/teacher/courses" replace />;
    case 'student':
      return <Navigate to="/student/courses" replace />;
    default:
      return <Navigate to="/" replace />;
  }
};

const UnauthorizedPage = () => {
  // If we know who they are, send them to their own dashboard instead
  // of dead-ending on a 403 screen. The common case: a student taps an
  // /admin/* link (broadcast email, bookmark, WhatsApp message), gets
  // bounced to /login?next=/admin/..., signs in, lands on /admin →
  // ProtectedRoute kicks them here. Showing them a red "403 غير مصرح"
  // page is hostile UX when the right answer is "go to your own
  // dashboard". Only unauthenticated visitors actually see the 403.
  const { user, isAuthenticated, loading } = useAuth();
  if (!loading && isAuthenticated && user) {
    const rawRole = user?.role?.name ?? user?.role ?? user?.roleName;
    const role = typeof rawRole === 'string' ? rawRole.trim().toLowerCase() : '';
    switch (role) {
      case 'admin':
      case 'superadmin':
        return <Navigate to="/admin" replace />;
      case 'staff':
      case 'manager':
        return <Navigate to="/staff" replace />;
      case 'teacher':
        return <Navigate to="/teacher" replace />;
      case 'marketing':
        return <Navigate to="/marketing" replace />;
      case 'student':
        return <Navigate to="/student" replace />;
      default:
        break;
    }
  }
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <h1 className="text-4xl font-bold text-destructive mb-4">403</h1>
        <p className="text-xl text-muted-foreground mb-6">غير مصرح لك بالوصول إلى هذه الصفحة</p>
        <a href="/" className="text-primary hover:underline">العودة للصفحة الرئيسية</a>
      </div>
    </div>
  );
};

const NotFoundPage = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="text-center">
      <h1 className="text-4xl font-bold text-muted-foreground mb-4">404</h1>
      <p className="text-xl text-muted-foreground mb-6">الصفحة غير موجودة</p>
      <a href="/" className="text-primary hover:underline">العودة للصفحة الرئيسية</a>
    </div>
  </div>
);

// Public event pages intentionally bypass the authenticated agreement overlay
// and the persistent portal chrome. They remain inside providers for branding
// and notification consistency, but never trigger a login flow.
const AgreementAwareRoutes = () => {
  const { pathname } = useLocation();
  if (pathname.startsWith('/surveys/') || pathname.startsWith('/open-days/') || pathname === '/tamkeen' || pathname === '/complete-registration') return <AppRoutes />;
  return <AgreementGate><AppRoutes /></AgreementGate>;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      {/* Public form-builder registration — no auth, no layout. */}
      <Route path="/register/:slug" element={<PublicRegistration />} />
      <Route path="/surveys/:slug" element={<PublicSurveyPage />} />
      <Route path="/complete-registration" element={<RegistrationCompletion />} />
      <Route path="/catalogue" element={<Catalogue />} />
      <Route path="/display/graduation" element={<GraduationSlideshow />} />
      <Route path="/open-days/:id" element={<OpenDay />} />
      <Route path="/tamkeen" element={<Tamkeen />} />
      <Route path="/" element={<RoleBasedRedirect />} />
      <Route path="/unauthorized" element={<UnauthorizedPage />} />
      {/* Bare /courses has no page — breadcrumbs on /courses/:id/content
          link to it as a parent. Send the user to their role-specific
          courses list instead of a 404. */}
      <Route path="/courses" element={<CoursesRedirect />} />

      {/* ================= Persistent app shell =================
          Every authenticated in-app page lives under this layout route.
          The chrome mounts once; pages render into its <Outlet>, and their
          own <DashboardLayout> wrappers become content-only pass-throughs
          (see ShellContext). Per-route role guards stay exactly as before. */}
      <Route element={<PersistentShell />}>
      <Route path="/admin/surveys" element={
        <ProtectedRoute allowedRoles={['admin', 'superadmin', 'manager']}><SurveysPage /></ProtectedRoute>
      } />
      <Route path="/admin/surveys/new" element={
        <ProtectedRoute allowedRoles={['admin', 'superadmin', 'manager']}><SurveysPage /></ProtectedRoute>
      } />
      <Route path="/admin/surveys/:id" element={
        <ProtectedRoute allowedRoles={['admin', 'superadmin', 'manager']}><SurveysPage /></ProtectedRoute>
      } />
      <Route path="/admin/surveys/:id/results" element={
        <ProtectedRoute allowedRoles={['admin', 'superadmin', 'manager']}><SurveysPage /></ProtectedRoute>
      } />
      {/* Current-user inbox only; no additional admin capability for marketing. */}
      <Route path="/notifications" element={
        <ProtectedRoute allowedRoles={['student', 'teacher', 'manager', 'staff', 'admin', 'superadmin', 'marketing']}>
          <NotificationsPage />
        </ProtectedRoute>
      } />
      <Route path="/whatsapp-inbox" element={
        <ProtectedRoute allowedRoles={['superadmin', 'admin', 'manager', 'staff', 'marketing']}>
          <WhatsappInboxPage />
        </ProtectedRoute>
      } />
      <Route path="/work-reports" element={
        <ProtectedRoute allowedRoles={['admin', 'superadmin', 'manager']}>
          <WorkReportsPage />
        </ProtectedRoute>
      } />

      {/* Shared contact page — available to all authenticated roles */}
      <Route path="/contact" element={
        <ProtectedRoute allowedRoles={['student', 'teacher', 'manager', 'staff', 'admin', 'superadmin', 'marketing']}>
          <ContactPage />
        </ProtectedRoute>
      } />
      
      {/* Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute allowedRoles={['admin', 'superadmin']}>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/calendar-events" element={
        <ProtectedRoute allowedRoles={['admin', 'superadmin', 'manager', 'staff']}>
          <CalendarEventsAdminPage />
        </ProtectedRoute>
      } />
      {/* Dedicated team-meetings page (marketing excluded, same as the
          calendar-events management guard). */}
      <Route path="/admin/meetings" element={
        <ProtectedRoute allowedRoles={['admin', 'superadmin', 'manager', 'staff']}>
          <TeamMeetingsPage />
        </ProtectedRoute>
      } />
      {/* Private calendars — office personal calendars w/ availability,
          sharing and slot booking. Staff can view calendars shared with
          them and book; only superadmin/admin/manager own one. */}
      <Route path="/admin/private-calendar" element={
        <ProtectedRoute allowedRoles={['admin', 'superadmin', 'manager', 'staff']}>
          <PrivateCalendarPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/staff-attendance" element={
        <ProtectedRoute allowedRoles={['admin', 'superadmin', 'manager', 'staff']}>
          <StaffAttendancePage />
        </ProtectedRoute>
      } />
      <Route path="/admin/leads" element={
        <ProtectedRoute allowedRoles={['admin', 'superadmin', 'manager', 'staff', 'marketing']}>
          <LeadsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/contacts" element={
        <ProtectedRoute allowedRoles={['admin', 'superadmin', 'manager', 'staff', 'marketing']}>
          <ContactsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/registration-forms" element={
        <ProtectedRoute allowedRoles={['admin', 'superadmin', 'manager', 'staff', 'marketing']}>
          <RegistrationFormsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/students" element={
        <ProtectedRoute allowedRoles={['admin', 'superadmin', 'manager', 'staff']}>
          <StudentsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/cohorts/:id" element={
        <ProtectedRoute allowedRoles={['admin', 'superadmin', 'manager', 'staff']}>
          <CohortDetail />
        </ProtectedRoute>
      } />
      <Route path="/admin/courses/:id" element={
        <ProtectedRoute allowedRoles={['admin', 'superadmin', 'manager', 'staff']}>
          <CourseDetail />
        </ProtectedRoute>
      } />
      <Route path="/admin/teachers" element={
        <ProtectedRoute allowedRoles={['admin', 'superadmin']}>
          <TeachersPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/users" element={
        <ProtectedRoute allowedRoles={['admin', 'superadmin']}>
          <UsersPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/payments" element={
        <ProtectedRoute allowedRoles={['admin', 'superadmin', 'manager', 'staff']}>
          <PaymentsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/students/:id/finance" element={
        <ProtectedRoute allowedRoles={['admin', 'superadmin', 'manager', 'staff']}>
          <StudentFinance />
        </ProtectedRoute>
      } />
      <Route path="/admin/payment-plans" element={
        <ProtectedRoute allowedRoles={['admin', 'superadmin', 'manager', 'staff']}>
          <PaymentPlansPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/pricing-protocol" element={
        <ProtectedRoute allowedRoles={['admin', 'superadmin']}>
          <PricingProtocolPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/messages" element={
        <ProtectedRoute allowedRoles={['admin', 'superadmin', 'manager', 'staff', 'marketing']}>
          <MessagesPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/email-templates" element={
        <ProtectedRoute allowedRoles={['admin', 'superadmin', 'manager']}>
          <EmailDesignerPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/booklet-editor" element={
        <ProtectedRoute capability="bookletEditorAccess">
          <BookletEditorPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/courses" element={
        <ProtectedRoute allowedRoles={['admin', 'superadmin', 'manager', 'staff']}>
          <CoursesPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/cohorts" element={
        <ProtectedRoute allowedRoles={['admin', 'superadmin', 'manager', 'staff']}>
          <CohortsPage />
        </ProtectedRoute>
      } />
      {/* The old standalone rosters page was merged into /admin/cohorts
          (tabs "حسب الفوج" / "حسب المساق"). Keep the URL working for
          bookmarks by redirecting into the merged page. */}
      <Route path="/admin/rosters" element={<Navigate to="/admin/cohorts?tab=byCohort" replace />} />
      <Route path="/admin/notifications" element={
        <ProtectedRoute allowedRoles={['admin', 'superadmin', 'manager', 'staff']}>
          <NotificationsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/zoom-backup" element={
        <ProtectedRoute allowedRoles={['admin', 'superadmin']}>
          <ZoomBackupPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/job-status" element={
        <ProtectedRoute allowedRoles={['admin', 'superadmin']}>
          <JobStatusPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/course-copy-file-incidents" element={
        <ProtectedRoute allowedRoles={['admin', 'superadmin']}>
          <CourseCopyFileIncidentsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/bunny-mirror" element={
        <ProtectedRoute allowedRoles={['admin', 'superadmin']}>
          <BunnyMirrorPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/zoom-meetings" element={
        <ProtectedRoute allowedRoles={['admin', 'superadmin']}>
          <ZoomMeetingsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/audit-logs" element={
        <ProtectedRoute allowedRoles={['admin', 'superadmin', 'manager', 'staff']}>
          <AuditLogsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/access-control" element={
        <ProtectedRoute allowedRoles={['admin', 'superadmin', 'manager']}>
          <AccessControlPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/logs" element={
        <ProtectedRoute allowedRoles={['admin', 'superadmin']}>
          <LogsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/recycle-bin" element={
        <ProtectedRoute allowedRoles={['admin', 'superadmin']}>
          <RecycleBinPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/programs" element={
        <ProtectedRoute allowedRoles={['admin', 'superadmin', 'manager', 'staff']}>
          <ProgramsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/agreements" element={
        <ProtectedRoute allowedRoles={['admin', 'superadmin', 'manager']}>
          <AgreementsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/settings" element={
        <ProtectedRoute allowedRoles={['admin', 'superadmin']}>
          <SettingsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/profile" element={
        <ProtectedRoute allowedRoles={['admin', 'superadmin', 'marketing']}>
          <AdminProfilePage />
        </ProtectedRoute>
      } />
      <Route path="/admin/global-schedule" element={
        <ProtectedRoute allowedRoles={['admin', 'superadmin', 'manager', 'staff']}>
          <GlobalSchedulePage />
        </ProtectedRoute>
      } />
      <Route path="/admin/schedule" element={
        <ProtectedRoute allowedRoles={['admin', 'superadmin', 'manager', 'staff', 'marketing']}>
          <SchedulePage />
        </ProtectedRoute>
      } />
      <Route path="/admin/tasks" element={
        <ProtectedRoute allowedRoles={['admin', 'superadmin', 'manager', 'staff', 'marketing']}>
          <TaskBoardPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/deletion-requests" element={
        <ProtectedRoute allowedRoles={['admin', 'superadmin', 'manager']}>
          <DeletionRequestsPage />
        </ProtectedRoute>
      } />

      {/* Website Manager hub removed — all sections live directly in the side nav. */}
      <Route path="/admin/website/departments" element={
        <ProtectedRoute allowedRoles={['admin', 'superadmin', 'manager', 'marketing']}>
          <WebsiteDepartments />
        </ProtectedRoute>
      } />
      <Route path="/admin/website/testimonials" element={
        <ProtectedRoute allowedRoles={['admin', 'superadmin', 'manager', 'marketing']}>
          <WebsiteTestimonials />
        </ProtectedRoute>
      } />
      <Route path="/admin/website/news" element={
        <ProtectedRoute allowedRoles={['admin', 'superadmin', 'manager', 'marketing']}>
          <WebsiteNews />
        </ProtectedRoute>
      } />
      <Route path="/admin/website/banners" element={
        <ProtectedRoute allowedRoles={['admin', 'superadmin', 'manager', 'marketing']}>
          <WebsiteBanners />
        </ProtectedRoute>
      } />
      <Route path="/admin/website/pages" element={
        <ProtectedRoute allowedRoles={['admin', 'superadmin', 'manager', 'marketing']}>
          <WebsitePages />
        </ProtectedRoute>
      } />
      <Route path="/admin/website/library" element={
        <ProtectedRoute allowedRoles={['admin', 'superadmin', 'manager', 'marketing']}>
          <WebsiteLibrary />
        </ProtectedRoute>
      } />
      <Route path="/admin/website/jobs" element={
        <ProtectedRoute allowedRoles={['admin', 'superadmin', 'manager', 'marketing']}>
          <WebsiteJobs />
        </ProtectedRoute>
      } />
      <Route path="/admin/website/display-playlists" element={
        <ProtectedRoute allowedRoles={['admin', 'superadmin', 'manager', 'marketing']}>
          <WebsiteDisplayPlaylists />
        </ProtectedRoute>
      } />
      <Route path="/admin/website/submissions" element={
        <ProtectedRoute allowedRoles={['admin', 'superadmin', 'manager']}>
          <WebsiteSubmissions />
        </ProtectedRoute>
      } />
      <Route path="/admin/website/contact-messages" element={<Navigate to="/admin/website/submissions" replace />} />
      <Route path="/admin/website/contact" element={<Navigate to="/admin/website/submissions" replace />} />
      <Route path="/admin/website/site-settings" element={
        <ProtectedRoute allowedRoles={['admin', 'superadmin', 'manager']}>
          <WebsiteSiteSettings />
        </ProtectedRoute>
      } />

      {/* Marketing Routes */}
      <Route path="/marketing" element={
        <ProtectedRoute allowedRoles={['marketing']}>
          <MarketingDashboard />
        </ProtectedRoute>
      } />

      {/* Staff Routes */}
      <Route path="/staff" element={
        <ProtectedRoute allowedRoles={['staff', 'manager', 'admin', 'superadmin']}>
          <StaffDashboard />
        </ProtectedRoute>
      } />
      <Route path="/staff/leads" element={
        <ProtectedRoute allowedRoles={['staff', 'manager', 'admin', 'superadmin']}>
          <LeadsPage />
        </ProtectedRoute>
      } />
      <Route path="/staff/contacts" element={
        <ProtectedRoute allowedRoles={['staff', 'manager', 'admin', 'superadmin']}>
          <ContactsPage />
        </ProtectedRoute>
      } />
      <Route path="/staff/students" element={
        <ProtectedRoute allowedRoles={['staff', 'manager', 'admin', 'superadmin']}>
          <StudentsPage />
        </ProtectedRoute>
      } />
      <Route path="/staff/teachers" element={
        <ProtectedRoute allowedRoles={['staff', 'manager', 'admin', 'superadmin']}>
          <TeachersPage />
        </ProtectedRoute>
      } />
      <Route path="/staff/payments" element={
        <ProtectedRoute allowedRoles={['staff', 'manager', 'admin', 'superadmin']}>
          <PaymentsPage />
        </ProtectedRoute>
      } />
      <Route path="/staff/messages" element={
        <ProtectedRoute allowedRoles={['staff', 'manager', 'admin', 'superadmin']}>
          <MessagesPage />
        </ProtectedRoute>
      } />
      <Route path="/staff/profile" element={
        <ProtectedRoute allowedRoles={['staff', 'manager', 'admin', 'superadmin']}>
          <AdminProfilePage />
        </ProtectedRoute>
      } />

      {/* Teacher Routes */}
      <Route path="/teacher" element={
        <ProtectedRoute allowedRoles={['teacher', 'admin', 'superadmin']}>
          <TeacherDashboard />
        </ProtectedRoute>
      } />
      <Route path="/teacher/students" element={
        <ProtectedRoute allowedRoles={['teacher', 'admin', 'superadmin']}>
          <TeacherStudentsPage />
        </ProtectedRoute>
      } />
      <Route path="/teacher/courses" element={
        <ProtectedRoute allowedRoles={['teacher', 'admin', 'superadmin']}>
          <TeacherCoursesPage />
        </ProtectedRoute>
      } />
      <Route path="/teacher/schedule" element={
        <ProtectedRoute allowedRoles={['teacher', 'admin', 'superadmin']}>
          <SchedulePage />
        </ProtectedRoute>
      } />
      <Route path="/teacher/notifications" element={
        <ProtectedRoute allowedRoles={['teacher', 'admin', 'superadmin']}>
          <TeacherNotificationsPage />
        </ProtectedRoute>
      } />
      <Route path="/teacher/profile" element={
        <ProtectedRoute allowedRoles={['teacher', 'admin', 'superadmin']}>
          <TeacherProfilePage />
        </ProtectedRoute>
      } />
      <Route path="/teacher/news" element={
        <ProtectedRoute allowedRoles={['teacher', 'admin', 'superadmin']}>
          <NewsEventsPage />
        </ProtectedRoute>
      } />
      <Route path="/teacher/library" element={
        <ProtectedRoute allowedRoles={['teacher', 'admin', 'superadmin']}>
          <LibraryPage />
        </ProtectedRoute>
      } />

      {/* Tabbed course shell (shared). One mounted shell keeps the persistent
          header + tab bar on screen; each tab renders into <Outlet>. The
          backend enforces enrolment per request on every tab's data calls, so
          any authenticated user can hit these routes and get filtered access.
          The old per-tab URLs (/content, /recordings, /assignments, /gradebook)
          keep working as nested routes; bare /courses/:id redirects to the
          remembered tab (or Overview). */}
      <Route path="/courses/:courseId" element={
        <ProtectedRoute allowedRoles={['student', 'teacher', 'manager', 'staff', 'admin', 'superadmin']}>
          <CourseShell />
        </ProtectedRoute>
      }>
        <Route index element={<CourseTabRedirect />} />
        <Route path="overview" element={<CourseOverview />} />
        <Route path="content" element={<CourseContent embedded />} />
        <Route path="recordings" element={<CourseRecordings embedded />} />
        <Route path="assignments" element={<AssignmentsPage embedded />} />
        <Route path="gradebook" element={<GradebookPage embedded />} />
        <Route path="certificate" element={<CertificateManager embedded />} />
        <Route path="certificate/editor" element={
          <ProtectedRoute allowedRoles={['teacher', 'manager', 'admin', 'superadmin']}>
            <CertificateEditor />
          </ProtectedRoute>
        } />
      </Route>
      {/* In-portal Bunny player. WatchRecording registers the video intent;
           RecordingPlayerProvider (mounted by PersistentShell) owns the
           iframe above this Outlet, so navigation can shrink it to a
           mini-player without mounting a second frame. Backend re-mints a
           fresh signed + IP-locked URL for each video intent. */}
      <Route path="/courses/:courseId/watch/:bunnyVideoId" element={
        <ProtectedRoute allowedRoles={['student', 'teacher', 'manager', 'staff', 'admin', 'superadmin']}>
          <WatchRecording />
        </ProtectedRoute>
      } />
      {/* Assignments + Gradebook now live as tabs inside CourseShell above
          (/courses/:id/assignments and /gradebook are nested routes). */}
      <Route path="/courses/:courseId/grading" element={
        <ProtectedRoute allowedRoles={['teacher', 'staff', 'manager', 'admin', 'superadmin']}>
          <CourseGradingPage />
        </ProtectedRoute>
      } />
      {/* Take-exam route. Students take the real exam (writes a Submission
          + Grade). Teachers/staff/admins see an ephemeral preview mode —
          they can answer and get a score, but nothing is persisted. The
          page itself reads the current user's role and switches modes. */}
      <Route path="/courses/:courseId/exam/:assignmentId" element={
        <ProtectedRoute allowedRoles={['student', 'teacher', 'manager', 'staff', 'admin', 'superadmin']}>
          <TakeExamPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/enrollments" element={
        <ProtectedRoute allowedRoles={['admin', 'superadmin', 'manager', 'staff']}>
          <EnrollmentsPage />
        </ProtectedRoute>
      } />

      {/* Student Routes */}
      <Route path="/student" element={
        <ProtectedRoute allowedRoles={['student', 'admin', 'superadmin']}>
          <StudentDashboard />
        </ProtectedRoute>
      } />
      <Route path="/student/courses" element={
        <ProtectedRoute allowedRoles={['student', 'admin', 'superadmin']}>
          <StudentCoursesPage />
        </ProtectedRoute>
      } />
      <Route path="/student/schedule" element={
        <ProtectedRoute allowedRoles={['student', 'admin', 'superadmin']}>
          <SchedulePage />
        </ProtectedRoute>
      } />
      <Route path="/student/payments" element={
        <ProtectedRoute allowedRoles={['student', 'admin', 'superadmin']}>
          <StudentPaymentsPage />
        </ProtectedRoute>
      } />
      <Route path="/student/grades" element={
        <ProtectedRoute allowedRoles={['student', 'admin', 'superadmin']}>
          <StudentGradesPage />
        </ProtectedRoute>
      } />
      <Route path="/student/certificates" element={
        <ProtectedRoute allowedRoles={['student', 'admin', 'superadmin']}>
          <StudentCertificatesPage />
        </ProtectedRoute>
      } />
      <Route path="/student/certificates/manual/:certificateId" element={
        <ProtectedRoute allowedRoles={['student', 'admin', 'superadmin']}>
          <StudentCertificateView />
        </ProtectedRoute>
      } />
      <Route path="/student/certificates/:courseId" element={
        <ProtectedRoute allowedRoles={['student', 'admin', 'superadmin']}>
          <StudentCertificateView />
        </ProtectedRoute>
      } />
      <Route path="/student/notifications" element={
        <ProtectedRoute allowedRoles={['student', 'admin', 'superadmin']}>
          <StudentNotificationsPage />
        </ProtectedRoute>
      } />
      <Route path="/student/profile" element={
        <ProtectedRoute allowedRoles={['student', 'admin', 'superadmin']}>
          <StudentProfilePage />
        </ProtectedRoute>
      } />
      <Route path="/student/news" element={
        <ProtectedRoute allowedRoles={['student', 'admin', 'superadmin']}>
          <NewsEventsPage />
        </ProtectedRoute>
      } />
      <Route path="/student/library" element={
        <ProtectedRoute allowedRoles={['student', 'admin', 'superadmin']}>
          <LibraryPage />
        </ProtectedRoute>
      } />

      </Route>
      {/* =============== end persistent app shell =============== */}

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

// Shown while a lazy route chunk downloads. Full-screen, branded spinner —
// route chunks are small so this rarely lingers, and it stays consistent for
// both public (login-adjacent) and in-app routes.
const RouteFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="flex flex-col items-center gap-4">
      <div className="h-12 w-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
      <p className="text-muted-foreground">جاري التحميل...</p>
    </div>
  </div>
);

function App() {
  // Replace native window.alert() with our toast notifier exactly once
  // on mount. This means EVERY legacy `alert('...')` call across the
  // codebase now renders as a non-blocking toast instead of a browser
  // popup, without touching the call sites individually.
  useEffect(() => { installAlertShim(); }, []);
  // Mobile-app WebView bridge: start listening for the phone's push token
  // as early as possible (even before login). Inert in normal browsers.
  useEffect(() => { initNativePushListeners(); }, []);
  return (
    <Router>
      <ScrollToTop />
      <SiteSettingsProvider>
        <AuthProvider>
          <BreadcrumbNamesProvider>
            <Suspense fallback={<RouteFallback />}>
              <AgreementAwareRoutes />
            </Suspense>
          </BreadcrumbNamesProvider>
          <Notifier />
        </AuthProvider>
      </SiteSettingsProvider>
    </Router>
  );
}

export default App;
